// Why a document is read only, shown above it wherever it is previewed or
// edited: "Locked by Ali — Sent for approval", "Being signed", "Filed as a
// record"… `reason` is the file read's `edit_block` (or the editor's
// 'no_edit_access'); nothing renders without one. `lock` is `lockOf(node)`.

import { FileCheck2, LockKeyhole, PenTool, UserCheck, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { isLockedByMe } from '@/shared/utils/nodeAccess.js';
import { fileStateText } from '@/shared/utils/fileStateText.js';

const ICONS = {
  locked: LockKeyhole,
  signed: PenTool,
  agreement: UserCheck,
  record: FileCheck2,
  no_edit_access: Eye,
};

export function FileStateBanner({ reason, lock = null, className }) {
  const { t } = useTranslation('editor');
  if (!reason) return null;
  const Icon = ICONS[reason] || Eye;
  const mine = reason === 'locked' && isLockedByMe({ lock });
  const text = fileStateText(t, reason, lock, { mine });
  return (
    <div
      role="status"
      data-testid="file-state-banner"
      data-reason={reason}
      className={cn(
        'flex flex-none items-center gap-2 border-b px-3 py-1.5 text-xs',
        reason === 'locked'
          ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200'
          : 'border-border bg-muted/60 text-foreground',
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate font-medium" title={text}>{text}</span>
      <span className="shrink-0 opacity-70">{t('editor:block.readOnly')}</span>
    </div>
  );
}
